import type { Material } from "@/types/Views/MaterialDatabase/Material";

/** Indicator type ids as the EPD service numbers them. */
export const GWP = 0;
export const GWP_TOTAL = 12;

/**
 * Whether an EPD record is a construction (a build-up of component EPDs) rather than a plain material.
 *
 * `isConstruction` is `bool?` on the backend while the frontend type declares it non-optional, so older
 * rows can arrive as null — test for `true` explicitly and let null fall through to the
 * `constructionEpds` check rather than reading it as either answer. Same test as `declaredEpdSource`.
 */
export const isConstructionMaterial = (material: Material): boolean =>
  material.isConstruction === true || (material.constructionEpds?.length ?? 0) > 0;

/**
 * The indicator type to actually read off a given record (RTLCA-4166).
 *
 * A construction carries a single unified GWP (type 0) and no GWP-Total (type 12): the API composes its
 * components into one figure using the same rule as the project breakdown, because a build-up that mixes
 * an EN 15804+A1 component (GWP only) with an +A2 one (GWP-Total only) has to add the two together
 * rather than show each under its own indicator. So for a construction, asking for GWP-Total means
 * asking for that one unified GWP. Plain materials are untouched and keep both indicators.
 */
export const resolveIndicatorType = (material: Material, indicatorType: number): number =>
  isConstructionMaterial(material) && indicatorType === GWP_TOTAL ? GWP : indicatorType;
